<template>
  <BaseLayout title="Students">
    <div class="container my-4">
      <h1 class="display-5 text-center mb-1">Students</h1>
      <p class="text-center text-muted mb-4">Search students by name or UCO</p>

      <!-- Search with autocomplete -->
      <div class="row justify-content-center mb-4">
        <div class="col-md-6 position-relative">
          <input
            v-model="query"
            type="text"
            class="form-control"
            placeholder="Name or UCO..."
            autocomplete="off"
            @focus="showDropdown = true"
            @blur="onBlur"
          />
          <!-- Autocomplete dropdown -->
          <div
            v-if="showDropdown && query && dropdownStudents.length"
            class="list-group position-absolute w-100 shadow-sm"
            style="z-index: 1000; max-height: 260px; overflow-y: auto;"
          >
            <button
              v-for="s in dropdownStudents"
              :key="s.uco"
              type="button"
              class="list-group-item list-group-item-action d-flex align-items-center gap-2 py-2"
              @mousedown.prevent="openStudent(s)"
            >
              <div
                class="avatar-circle-sm d-flex align-items-center justify-content-center flex-shrink-0"
                :style="{ background: avatarColor(s) }"
              >
                <span class="text-white fw-bold" style="font-size:0.7rem;">{{ avatarLabel(s) }}</span>
              </div>
              <span class="fw-semibold flex-grow-1">{{ s.name || '(no name)' }}</span>
              <span class="badge bg-secondary">{{ s.uco }}</span>
            </button>
          </div>
          <div
            v-if="showDropdown && query && !dropdownStudents.length && !loading"
            class="list-group position-absolute w-100 shadow-sm"
            style="z-index: 1000;"
          >
            <div class="list-group-item text-muted small">No students found.</div>
          </div>
        </div>
      </div>

      <div v-if="loading" class="text-center my-4">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <div v-else>
        <div v-if="filteredStudents.length === 0 && !query" class="text-center text-muted">
          No students found.
        </div>

        <!-- Student list -->
        <div
          v-for="student in filteredStudents"
          :key="student.uco"
          class="card mb-2 student-row"
          style="cursor: pointer;"
          @click="openStudent(student)"
        >
          <div class="card-body d-flex align-items-center gap-3 py-2">
            <!-- Avatar circle -->
            <div
              class="avatar-circle d-flex align-items-center justify-content-center flex-shrink-0"
              :style="{ background: avatarColor(student) }"
            >
              <span class="text-white fw-bold small">{{ avatarLabel(student) }}</span>
            </div>

            <!-- Name + UCO -->
            <div class="flex-grow-1">
              <div class="fw-semibold">{{ student.name || '(no name)' }}</div>
              <div class="text-muted small">UCO: {{ student.uco }}</div>
            </div>

            <!-- Progress badge -->
            <span
              class="badge"
              :class="progressBadgeClass(student)"
            >{{ progressBadgeText(student) }}</span>
          </div>
        </div>
      </div>
    </div>

    <StemErrorModal ref="stemErrorModal" />
  </BaseLayout>
</template>

<script>
import BaseLayout from '@/components/BaseLayout.vue';
import StemErrorModal from '@/components/StemErrorModal.vue';
import StudentController from '@/controllers/StudentController';
import { mapGetters } from 'vuex';

const AVATAR_COLORS = [
  '#4f86c6', '#5b8c5a', '#b85c38', '#7b5ea7', '#3a7ca5',
  '#c2763a', '#3d7a6b', '#a94466', '#6b7c3f', '#5a5e9a',
];

export default {
  name: 'StudentPage',
  components: { BaseLayout, StemErrorModal },
  data() {
    return {
      students: [],
      query: '',
      loading: true,
      showDropdown: false,
    };
  },
  computed: {
    ...mapGetters(['semesterId']),
    sortedStudents() {
      return [...this.students].sort((a, b) =>
        (a.name ?? '').localeCompare(b.name ?? '')
      );
    },
    filteredStudents() {
      const q = this.query.toLowerCase().trim();
      if (!q) return this.sortedStudents;
      return this.sortedStudents.filter(
        s => (s.name ?? '').toLowerCase().includes(q) || String(s.uco).includes(q)
      );
    },
    dropdownStudents() {
      return this.filteredStudents.slice(0, 8);
    },
  },
  async created() {
    try {
      const [raw, summaries] = await Promise.all([
        StudentController.fetchAll(this.semesterId),
        StudentController.fetchProgressSummaries(this.semesterId),
      ]);
      const summaryByUco = Object.fromEntries(summaries.map(s => [s.uco, s]));
      this.students = raw.map(s => ({ ...s, summary: summaryByUco[s.uco] ?? null }));
    } catch (error) {
      this.$refs.stemErrorModal.show('Error', error.message);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    openStudent(student) {
      this.showDropdown = false;
      this.query = '';
      this.$router.push(`/students/${student.uco}/detail`);
    },
    onBlur() {
      // small delay so mousedown on item fires first
      setTimeout(() => { this.showDropdown = false; }, 150);
    },
    avatarLabel(student) {
      const idx = this.sortedStudents.indexOf(student);
      return 'S' + (idx + 1);
    },
    avatarColor(student) {
      const idx = this.sortedStudents.indexOf(student);
      return AVATAR_COLORS[idx % AVATAR_COLORS.length];
    },
    progressBadgeText(student) {
      if (!student.summary) return '...';
      const ev = student.summary.evaluated_subtask_count;
      const total = student.summary.total_subtask_count;
      if (ev === 0) return 'Not started';
      return `${ev}/${total} evaluated`;
    },
    progressBadgeClass(student) {
      if (!student.summary) return 'bg-secondary';
      const ev = student.summary.evaluated_subtask_count;
      const total = student.summary.total_subtask_count;
      if (ev === 0) return 'bg-secondary';
      if (ev >= total) return 'bg-success';
      return 'bg-warning text-dark';
    },
  },
};
</script>

<style scoped>
.avatar-circle {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  font-size: 0.8rem;
}
.avatar-circle-sm {
  width: 28px;
  height: 28px;
  border-radius: 50%;
}
.student-row:hover {
  background-color: rgba(0, 0, 0, 0.04);
}
</style>
